// ~/apps/themusicchurch-app/frontend/src/pages/Ministries/CareMusicTours.jsx

import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiMusic,
  FiHeart,
  FiMapPin,
  FiUsers,
  FiCalendar,
} from "react-icons/fi";
import Button from "../../components/Common/Button";
import ResponsiveBanner from "../../components/Common/ResponsiveBanner";

const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${(props) => props.theme.colors.homeNavy};
  color: ${(props) => props.theme.colors.homeText};

  h1, h2, h3, h4, h5, h6 { color: ${(props) => props.theme.colors.homeGold}; }
  a:focus-visible, button:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.accent};
    outline-offset: 4px;
  }
`;

// Title section with minimal padding - consistent with other pages
const TitleSection = styled.section`
  padding: ${(props) => props.theme.spacing.xl}
    ${(props) => props.theme.spacing.lg};
  text-align: center;
  background: ${(props) => props.theme.colors.homeNavy};

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: ${(props) => props.theme.spacing.lg}
      ${(props) => props.theme.spacing.lg};
  }
`;

const TitleContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  color: ${(props) => props.theme.colors.homeText};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  margin-bottom: ${(props) => props.theme.spacing.lg};
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    color: ${(props) => props.theme.colors.homeGold};
  }
`;

const MinistryTitle = styled(motion.h1)`
  font-size: ${(props) => props.theme.fontSizes["5xl"]};
  color: ${(props) => props.theme.colors.homeGold};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const MinistryTagline = styled(motion.p)`
  font-size: ${(props) => props.theme.fontSizes.xl};
  color: ${(props) => props.theme.colors.homeText};
  max-width: 600px;
  margin: 0 auto;
`;

const ContentSection = styled.section`
  padding: ${(props) => props.theme.spacing["4xl"]}
    ${(props) => props.theme.spacing.lg};
  max-width: 1200px;
  margin: 0 auto;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${(props) => props.theme.spacing["4xl"]};

  @media (min-width: ${(props) => props.theme.breakpoints.desktop}) {
    grid-template-columns: 2fr 1fr;
  }
`;

const MainContent = styled(motion.div)``;

const Sidebar = styled(motion.div)``;

const Section = styled.div`
  margin-bottom: ${(props) => props.theme.spacing["3xl"]};
`;

const SectionTitle = styled.h2`
  color: ${(props) => props.theme.colors.homeGold};
  font-size: ${(props) => props.theme.fontSizes["3xl"]};
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const Text = styled.p`
  color: ${(props) => props.theme.colors.homeText};
  line-height: 1.8;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const QuoteBox = styled.blockquote`
  background: ${(props) => props.theme.colors.homeSurface};
  border-left: 4px solid ${(props) => props.theme.colors.accent};
  padding: ${(props) => props.theme.spacing.xl};
  margin: ${(props) => props.theme.spacing["2xl"]} 0;
  font-style: italic;
  color: ${(props) => props.theme.colors.homeText};
`;

const VenueGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${(props) => props.theme.spacing.lg};
  margin-top: ${(props) => props.theme.spacing["2xl"]};
`;

const VenueCard = styled.div`
  background: ${(props) => props.theme.colors.homeSurface};
  padding: ${(props) => props.theme.spacing.lg};
  border-radius: 12px;
  text-align: center;
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    background: ${(props) => props.theme.colors.accent}20;
    transform: translateY(-2px);
  }
`;

const VenueIcon = styled.div`
  font-size: ${(props) => props.theme.fontSizes["2xl"]};
  color: ${(props) => props.theme.colors.accent};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const VenueTitle = styled.h4`
  color: ${(props) => props.theme.colors.homeGold};
  font-size: ${(props) => props.theme.fontSizes.base};
  margin-bottom: ${(props) => props.theme.spacing.xs};
`;

const InfoCard = styled.div`
  background: ${(props) => props.theme.colors.homeSurface};
  border-radius: 12px;
  padding: ${(props) => props.theme.spacing.xl};
  box-shadow: ${(props) => props.theme.shadows.md};
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${(props) => props.theme.spacing.md};
  margin-bottom: ${(props) => props.theme.spacing.md};

  &:last-child {
    margin-bottom: 0;
  }

  svg {
    color: ${(props) => props.theme.colors.accent};
    font-size: ${(props) => props.theme.fontSizes.xl};
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const MusicStylesList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing.sm};
  margin-top: ${(props) => props.theme.spacing.lg};
`;

const MusicStyleTag = styled.li`
  background: ${(props) => props.theme.colors.accent}20;
  color: ${(props) => props.theme.colors.homeGold};
  padding: ${(props) => props.theme.spacing.xs}
    ${(props) => props.theme.spacing.md};
  border-radius: 20px;
  font-size: ${(props) => props.theme.fontSizes.sm};
`;

const ImpactBox = styled.div`
  background: ${(props) => props.theme.colors.homeSurface};
  color: white;
  padding: ${(props) => props.theme.spacing["2xl"]};
  border-radius: 12px;
  margin: ${(props) => props.theme.spacing["2xl"]} 0;
  text-align: center;
`;

const CTASection = styled(motion.div)`
  background: ${(props) => props.theme.colors.homeSurface};
  color: white;
  padding: ${(props) => props.theme.spacing["3xl"]};
  border-radius: 16px;
  text-align: center;
  margin-top: ${(props) => props.theme.spacing["4xl"]};
`;

const CareMusicTours = () => {
  const venues = [
    { icon: "🏥", title: "Hospitals" },
    { icon: "🏠", title: "Nursing Homes" },
    { icon: "⛪", title: "Orphanages" },
    { icon: "🔒", title: "Prisons" },
    { icon: "🏛️", title: "Mental Institutions" },
    { icon: "🌳", title: "Public Parks" },
  ];

  const musicStyles = [
    "Wedding Bands",
    "Gospel Groups",
    "Jazz Ensembles",
    "Rock Bands",
    "Country Music",
    "Classical",
    "Hip-Hop",
    "Reggae",
    "Pop Groups",
    "DJs",
  ];

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Care Music Tours - The Music Church</title>
        <meta
          name="description"
          content="Care Music Tours - Live music concert series bringing joy, healing and peace to those restricted by illness, age, disease, and imprisonment."
        />
      </Helmet>

      <PageWrapper>
        {/* Title section above the banner */}
        <TitleSection>
          <TitleContent>
            <MinistryTitle
              variants={titleVariants}
              initial="hidden"
              animate="visible"
            >
              Care Music Tours
            </MinistryTitle>
            <MinistryTagline
              variants={titleVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.2 }}
            >
              Bringing healing through the gift of music
            </MinistryTagline>
          </TitleContent>
        </TitleSection>

        {/* Banner using ResponsiveBanner */}
        <ResponsiveBanner
          src="/images/home-preview/care-paired-singers-v51.png"
          alt="Care Music Tours - Live music concert series"
          maxWidth="800px"
          motionProps={{
            initial: { opacity: 0, y: 30 },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 0.8, delay: 0.3 },
          }}
        />

        <ContentSection>
          <ContentGrid>
            <MainContent
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <Section>
                <SectionTitle>About Care Music Tours</SectionTitle>
                <Text>
                  Care Music Tours is a non-profit venture dedicated to using
                  all music for ministry. We bring joy, healing, and peace
                  through live music performances, offering a doorway to relax
                  and break away from stress for those who need it most.
                </Text>
                <Text>
                  We care by sharing, playing, and performing live music to
                  those restricted by illness, age, disease, and imprisonment.
                  Our mission is to reach the forgotten, the lonely, and the
                  hurting with the healing power of music and God's love.
                </Text>
              </Section>

              <QuoteBox>
                "For I was hungry and you gave me food to eat. I was thirsty and
                you gave me drink. I was a stranger and you took me in. I was
                naked and you clothed me. I was sick and you took care of me. I
                was in prison and you visited me."
                <br />- Matthew 25:35-36
              </QuoteBox>

              <Section>
                <SectionTitle>Where We Serve</SectionTitle>
                <Text>
                  Our concerts reach those who cannot come to us, so we go to
                  them:
                </Text>
                <VenueGrid>
                  {venues.map((venue, index) => (
                    <VenueCard key={index}>
                      <VenueIcon>{venue.icon}</VenueIcon>
                      <VenueTitle>{venue.title}</VenueTitle>
                    </VenueCard>
                  ))}
                </VenueGrid>
              </Section>

              <Section>
                <SectionTitle>Our Approach</SectionTitle>
                <Text>
                  We partner with talented performers from all musical
                  backgrounds who are willing to use their gifts to serve
                  others. Our concerts feature:
                </Text>
                <ul
                  style={{
                    marginLeft: "2rem",
                    color: "#c6d9e2",
                    lineHeight: "2",
                  }}
                >
                  <li>Positive, uplifting messages only</li>
                  <li>Music tailored to each venue's needs</li>
                  <li>Interactive performances to engage audiences</li>
                  <li>Prayer and spiritual encouragement</li>
                  <li>A message of hope and God's love</li>
                </ul>
              </Section>

              <ImpactBox>
                <h3 style={{ marginBottom: "1rem", fontSize: "1.5rem" }}>
                  Making a Difference
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  Every performance is an opportunity to touch hearts, lift
                  spirits, and remind people that they are not forgotten. Music
                  transcends barriers and speaks directly to the soul.
                </p>
              </ImpactBox>

              <Section>
                <SectionTitle>Musical Diversity</SectionTitle>
                <Text>
                  We welcome musicians from all genres and backgrounds:
                </Text>
                <MusicStylesList>
                  {musicStyles.map((style, index) => (
                    <MusicStyleTag key={index}>{style}</MusicStyleTag>
                  ))}
                </MusicStylesList>
              </Section>
            </MainContent>

            <Sidebar
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Get Involved
                </SectionTitle>
                <InfoItem>
                  <FiMusic />
                  <div>
                    <strong>Musicians</strong>
                    <p style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}>
                      Share your talent to bring joy to others
                    </p>
                  </div>
                </InfoItem>
                <InfoItem>
                  <FiCalendar />
                  <div>
                    <strong>Schedule a Visit</strong>
                    <p style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}>
                      Request a concert for your facility
                    </p>
                  </div>
                </InfoItem>
                <InfoItem>
                  <FiHeart />
                  <div>
                    <strong>Volunteer</strong>
                    <p style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}>
                      Help with logistics and support
                    </p>
                  </div>
                </InfoItem>
              </InfoCard>

              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Our Partners
                </SectionTitle>
                <Text style={{ fontSize: "0.875rem" }}>
                  We work with various organizations to reach those in need:
                </Text>
                <ul
                  style={{
                    fontSize: "0.875rem",
                    color: "#c6d9e2",
                    paddingLeft: "1.5rem",
                    marginTop: "0.5rem",
                  }}
                >
                  <li>Church organizations</li>
                  <li>Community outreach groups</li>
                  <li>State & federal chaplains</li>
                  <li>Artist management agencies</li>
                  <li>Local ministries</li>
                </ul>
              </InfoCard>

              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Content Guidelines
                </SectionTitle>
                <Text style={{ fontSize: "0.875rem" }}>
                  We adhere to strict content guidelines to ensure all
                  performances are appropriate and uplifting:
                </Text>
                <ul
                  style={{
                    fontSize: "0.875rem",
                    color: "#c6d9e2",
                    paddingLeft: "1.5rem",
                    marginTop: "0.5rem",
                  }}
                >
                  <li>Positive messages only</li>
                  <li>Venue-appropriate content</li>
                  <li>Respectful of all beliefs</li>
                  <li>Focus on hope & healing</li>
                </ul>
              </InfoCard>
            </Sidebar>
          </ContentGrid>

          <CTASection
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h3 style={{ fontSize: "2rem", marginBottom: "1rem" }}>
              Join Us in Bringing Music to Those Who Need It Most
            </h3>
            <p style={{ marginBottom: "1.5rem" }}>
              Whether you're a musician, a facility manager, or someone who
              wants to help, we'd love to hear from you.
            </p>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="accent"
                size="large"
                icon={<FiMusic />}
                onClick={() => console.log("Volunteer clicked")}
              >
                Volunteer as a Musician
              </Button>
              <Button
                variant="outline"
                size="large"
                style={{ borderColor: "white", color: "white" }}
                icon={<FiMapPin />}
                onClick={() => console.log("Request concert clicked")}
              >
                Request a Concert
              </Button>
            </div>
          </CTASection>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default CareMusicTours;
